import {
  gsapWithCSS,
  ScrollToPlugin,
  fromCamel,
  range,
  imagesLoaded,
} from "./gw.js";
import { GWElement } from "./elements.js";
import { getSectionId } from "./shopify-utils.js";
import "./parsed-element.js";
gsapWithCSS.registerPlugin(ScrollToPlugin);
const PAGE_STATUSES = {
  fetching: "fetching",
  ready: "ready",
  complete: "complete",
};
class InfiniteGrid extends GWElement {
  _loading = false;
  _rendering = false;
  pages = {};
  renderQueue = [];
  prerenderedItems = [];
  prerenderedPage = null;
  // Track which page has pre-rendered items
  constructor() {
    super();
    this.setBaseUrl();
    this.sectionId = getSectionId(this);
  }
  get optionDefaults() {
    return {
      manual: false,
      offset: 2e3,
      itemsPerPage: null,
      type: "product",
      cardOriginUrl: null,
      cardOriginName: null,
      selectors: {
        grid: "[data-columns], .gw-grid, .grid, .product-grid",
        gridItems: null,
        loadMore: "load-more",
        stickyHeader: "sticky-header",
      },
    };
  }
  derivedOptions(options) {
    return {
      identifier: `${options.type}Id`,
      selectors: {
        card: `[data-gw-${options.type}-card-selector]`,
      },
    };
  }
  static get observedAttributes() {
    return ["page"];
  }
  attributeChangedCallback(name, previousValue, value) {
    if (name === "page" && Number(value) !== this.page) {
      this.page = Number(value);
    }
  }
  setBaseUrl() {
    const url = new URL(location);
    url.searchParams.delete("page");
    this.baseURL = url;
  }
  init() {
    super.init();
    console.debug("Infinite Grid | Initializing...");
    this.id = `InfiniteGrid-${this.sectionId}`;
    this.itemsPerPage =
      this.options.itemsPerPage ?? this.gridItemElements.length;
    this.loadedPages ?? (this.loadedPages = []);
    this.renderedPages ?? (this.renderedPages = []);
    this.cardPageTriggers ??
      (this.cardPageTriggers = /* @__PURE__ */ new Map());
    this.pageTriggerCards ??
      (this.pageTriggerCards = /* @__PURE__ */ new Map());
    this.observer = new IntersectionObserver(this.observe.bind(this), {
      rootMargin: `0px 0px ${this.options.offset}px 0px`,
    });
    this.pageTriggerObserver = new IntersectionObserver(
      this.observePageTriggers.bind(this),
    );
    this.handleClick = this.onClick.bind(this);
    if (!this.pagination) {
      console.warn("Infinite Grid | No pagination element found on init.");
      return;
    }
    console.debug("Infinite Grid | Pagination: Connecting...");
    this.initCardSliderObserver();
    this.pagination.connect(this);
    this.pages[this.page] = {
      page: this.page,
      status: PAGE_STATUSES.complete,
    };
    this.pagination.bindEvents(this.handleClick);
    this.activateObserver();
    if (this.page > 1) {
      this.setPageAnchor(
        this.getElement("card", {
          parent: this.gridElement,
          cache: false,
        }),
        this.page,
      );
      this.loadPreviousPages();
    }
    window.addEventListener("load", this.fetchOnIdle.bind(this));
    this.addEventListener("click", this.onGridClick.bind(this), true);
  }
  onGridClick(e) {
    if (e.target.localName === "a" && e.target.closest(".card-wrapper")) {
      const productOrigin = {
        url: this.options.cardOriginUrl,
        name: this.options.cardOriginName,
        product: e.target.href.split("/").pop(),
        page: this.page,
      };
      sessionStorage.setItem("productOrigin", JSON.stringify(productOrigin));
      const cardWrapper = e.target.closest(".card-wrapper");
      sessionStorage.setItem("gridProductCardId", cardWrapper.id);
      const cardOffset = cardWrapper.getBoundingClientRect().top;
      sessionStorage.setItem("gridProductCardOffset", Math.round(cardOffset));
      let currentPageFirstCard = this.pageTriggerCards.get(this.page);
      if (!currentPageFirstCard) return;
      if (currentPageFirstCard.offsetTop > cardWrapper.offsetTop) {
        currentPageFirstCard = this.pageTriggerCards.get(this.page - 1);
      }
      const relativeCardOffset =
        cardOffset - currentPageFirstCard.getBoundingClientRect().top;
      sessionStorage.setItem(
        "gridProductCardRelativeOffset",
        Math.round(relativeCardOffset),
      );
    }
  }
  initCardSliderObserver() {
    const items = this.getItemsFromContainer(this);
    this.cardSliderObserver = new IntersectionObserver(
      this.onCardObservation.bind(this),
    );
    this.observeItems(items);
  }
  observeItems(items) {
    for (const item of items) {
      this.cardSliderObserver.observe(item);
    }
  }
  onCardObservation(entries, observer) {
    for (const entry of entries) {
      InfiniteGrid.toggleCardSlider(entry.target, entry.isIntersecting);
    }
  }
  static toggleCardSlider(item, enable = true) {
    const slider = item.querySelector(".slider");
    if (!slider) return;
    slider.classList.toggle("is-sleeping", !enable);
  }
  get loading() {
    return this._loading;
  }
  set loading(value) {
    var _a, _b;
    value = !!value;
    this._loading = value;
    if (value) {
      (_a = this.link) == null ? void 0 : _a.classList.add("loading");
    } else if (!this.rendering) {
      (_b = this.link) == null ? void 0 : _b.classList.remove("loading");
    }
  }
  get rendering() {
    return this._rendering;
  }
  set rendering(value) {
    var _a, _b;
    value = !!value;
    this._rendering = value;
    if (value) {
      (_a = this.link) == null ? void 0 : _a.classList.add("loading");
    } else if (!this.loading) {
      (_b = this.link) == null ? void 0 : _b.classList.remove("loading");
    }
  }
  get page() {
    return this._page ?? this.getAttribute("page") ?? 1;
  }
  get nextPage() {
    let nextPageNumber = this.getUrlPageNumber(this.nextUrl);
    if (!nextPageNumber || !/^\d+$/.test(nextPageNumber)) {
      console.warn(
        "Failed to determine next page number from URL:",
        this.nextUrl,
      );
      nextPageNumber = this.page + 1;
    }
    return nextPageNumber;
  }
  getUrlPageNumber(url) {
    if (!url) return null;
    return Number(url.split("page=").pop().split("&").shift());
  }
  set page(value) {
    this._page = value;
    this.setAttribute("page", this._page);
    const url = new URL(location);
    if (this._page !== 1) {
      url.searchParams.set("page", this._page);
    } else {
      url.searchParams.delete("page");
    }
    history.pushState({}, "", url);
  }
  get automatic() {
    return !this.options.manual;
  }
  get gridElement() {
    return this.getElement("grid");
  }
  get gridItemElements() {
    return this.getElement("gridItems", {
      all: true,
      parent: this.gridElement,
      cache: false,
    });
  }
  get gridItemCards() {
    return this.getElement("card", {
      all: true,
      parent: this.gridElement,
      cache: false,
    });
  }
  get gridItemIds() {
    return this.getItemIds(this.gridItemElements);
  }
  get pagination() {
    return this.getElement("loadMore");
  }
  getElementFromContainer(parent, key, { all = false } = {}) {
    return this.getElement(key, {
      parent,
      all,
      cache: false,
    });
  }
  getItemsFromContainer(container) {
    if (!container) {
      console.warn(
        "Infinite Grid | Failed to get items from container, no container provided",
      );
      return null;
    }
    const grid = this.getElementFromContainer(container, "grid");
    return this.getElementFromContainer(grid, "gridItems", {
      all: true,
    });
  }
  // TODO: Update and abstract into core element method
  getElement(key, { all = false, parent = this, cache = true } = {}) {
    const selector = this.options.selectors[key];
    let result;
    if (selector) {
      const method = all ? "querySelectorAll" : "querySelector";
      result = parent[method](selector);
    } else {
      result = parent.children;
    }
    if (!result) return null;
    const returnValue = all ? [...result] : result;
    return cache ? this.cacheElement(key, returnValue) : returnValue;
  }
  cacheElement(key, value) {
    var _a;
    if (!key.startsWith("_")) key = `_${key}`;
    this.elements ?? (this.elements = {});
    (_a = this.elements)[key] ?? (_a[key] = value);
    return this.elements[key];
  }
  clearElementCache() {
    this._grid = void 0;
    this._gridItems = void 0;
    this._loadMore = void 0;
  }
  selectById(id) {
    return this.gridElement.querySelector(
      `[data-${fromCamel(this.options.identifier)}="${id}"]`,
    );
  }
  setPageAnchor(cardId, page) {
    const card =
      cardId instanceof HTMLElement ? cardId : this.selectById(cardId);
    if (!card) return;
    console.debug("Infinite Grid | Setting page anchor", card, page);
    this.cardPageTriggers.set(card, Number(page));
    this.pageTriggerCards.set(Number(page), card);
    this.pageTriggerObserver.observe(card);
  }
  observe(entries, observer) {
    var _a;
    if (this.isBackfilling) return;
    const entry = entries[0];
    if (entry.isIntersecting) {
      let nextPage = this.nextPage;
      while (
        ((_a = this.pages[nextPage]) == null ? void 0 : _a.status) ===
        PAGE_STATUSES.complete
      ) {
        console.debug(
          "Infinite Grid | Observe: Next page already loaded. Incrementing next page...",
        );
        nextPage++;
      }
      console.debug(
        "Infinite Grid | Observe: Checking page status",
        nextPage,
        this.pages[nextPage],
      );
      if (this.pageNumbers.includes(nextPage)) {
        if (this.automatic) this.renderPage(nextPage);
      } else {
        this.load({
          page: nextPage,
          render: this.automatic,
        });
      }
      observer.disconnect();
    }
  }
  // Update current page based on scroll direction and trigger visibility
  observePageTriggers(entries, observer) {
    const entry = entries[0];
    if (this.isBackfilling) return;
    const pageModifier =
      !entry.isIntersecting && window.scrollDirection === "up" ? -1 : 0;
    this.setPageFromTrigger(entry.target, pageModifier);
  }
  setPageFromTrigger(trigger, modifier = 0) {
    const page = Math.max(1, this.cardPageTriggers.get(trigger) + modifier);
    if (page !== this.page) {
      this.page = page;
    }
  }
  get link() {
    var _a;
    return ((_a = this.pagination) == null ? void 0 : _a.link) ?? null;
  }
  get nextUrl() {
    var _a;
    return ((_a = this.pagination) == null ? void 0 : _a.url) ?? null;
  }
  async loadPreviousPages() {
    this.isBackfilling = true;
    const firstItem = this.getElement("card", {
      parent: this.gridElement,
      cache: false,
    });
    const stickyHeader = this.getElement("stickyHeader", {
      parent: document,
    });
    const previousPages = range(1, this.page - 1).toReversed();
    console.debug("Infinite Grid | Loading previous pages...");
    for (const page of previousPages) {
      const publishEvent = false;
      const prepend = true;
      console.debug("Infinite Grid | Loading previous page", page);
      await this.load({
        render: false,
        page,
        publishEvent,
        prepend,
      });
      console.debug(`Infinite Grid | Previous page ${page} loaded!`);
      this.updateGridItems(page);
      this.pages[page].status = PAGE_STATUSES.complete;
      setTimeout(() => {
        document.body.classList.add("has-sticky-header");
        stickyHeader.parentElement.classList.add(
          "shopify-section-header-sticky",
          "animate",
        );
        stickyHeader.parentElement.classList.remove(
          "shopify-section-header-hidden",
        );
        if (
          !sessionStorage.getItem("gridProductCardId") &&
          window.scrollY === 0
        ) {
          gsapWithCSS.to(window, {
            scrollTo: {
              y: firstItem,
              offsetY:
                (stickyHeader == null ? void 0 : stickyHeader.offsetHeight) +
                12,
            },
            duration: 0,
          });
        }
        if (page === previousPages[previousPages.length - 1]) {
          this.preloadedPage = null;
          setTimeout(() => {
            this.isBackfilling = false;
          });
        }
        this.dispatchRenderEvent(page, {
          backfill: true,
          backfillComplete: page === previousPages[previousPages.length - 1],
        });
      });
    }
  }
  get pageNumbers() {
    return Object.keys(this.pages).map(Number);
  }
  getPageUrl(page, { returnObj = false } = {}) {
    page = Number(page);
    const url = this.baseURL;
    if (page === 1) {
      url.searchParams.delete("page");
    } else {
      url.searchParams.set("page", page);
    }
    return returnObj ? url : url.toString();
  }
  async load({
    page = this.nextPage,
    render = false,
    publishEvent = true,
    prepend = false,
  } = {}) {
    if (this.pageNumbers.includes(page)) {
      console.debug(
        `Infinite Grid | Load More: Page ${page} already triggered load. Skipping...`,
      );
      return;
    }
    const url = this.getPageUrl(page, {
      returnObj: true,
    });
    this.pages[page] = {
      page,
      url: url.toString(),
      status: "fetching",
      prepend,
      content: null,
    };
    url.searchParams.set("section_id", this.sectionId);
    try {
      console.debug(
        `Infinite Grid | Load More: Fetch triggered. Fetching page ${page} from "${url}"...`,
      );
      this.loading = true;
      const response = await fetch(url);
      const markup = await response.text();
      const parser = new DOMParser();
      const content = parser
        .parseFromString(markup, "text/html")
        .querySelector("infinite-grid");
      this.setPageContent(page, content);
      console.debug(
        `Infinite Grid | Load More: Fetch complete. Page ${page} fetched.`,
      );
      if (publishEvent) {
        this.dispatchFetchEvent(page);
      }
      if (render) {
        this.renderPage(page);
      } else {
        this.loading = false;
      }
    } catch (error) {
      console.error("Infinite Grid | Load More: Error fetching page:", error);
      this.loading = false;
    }
  }
  setPageContent(page, content) {
    this.pages[page].content = content;
    this.pages[page].status = PAGE_STATUSES.ready;
  }
  async waitForPage(page, event = "fetch") {
    const eventName = `infinite-grid:${event}`;
    return new Promise((resolve) => {
      const listenerCallback = (e) => {
        const { id, page: eventPage } = e.detail;
        if (id === this.id && eventPage === page) {
          this.removeEventListener(eventName, listenerCallback);
          console.info(
            `Infinite Grid | Wait for Page: Page ${page} has ${event}ed.`,
          );
          resolve();
        }
      };
      this.addEventListener(eventName, listenerCallback);
    });
  }
  async renderPage(page = this.nextPage) {
    var _a, _b;
    this.rendering = true;
    this.loading = false;
    (_a = this.link) == null ? void 0 : _a.classList.add("loading");
    if (!page) {
      if (!this.renderQueue.length) {
        console.debug(
          "Infinite Grid | Render Page: Render queue empty. Skipping render...",
        );
        return;
      }
      page = this.renderQueue.shift();
    }
    const { status, content, prepend } = this.pages[page];
    const adjacentPage = this.pages[page + (prepend ? 1 : -1)];
    if (adjacentPage.status !== PAGE_STATUSES.complete) {
      console.warn(
        "Infinite Grid | Render Page: Previous page not yet rendered. Waiting...",
      );
      await this.waitForPage(adjacentPage, "render");
    }
    if (status !== PAGE_STATUSES.ready) {
      if (status === PAGE_STATUSES.complete) {
        return this.renderPage(page + 1);
      }
      if (status === PAGE_STATUSES.fetching) {
        console.debug(
          "Infinite Grid | Render Page: Page is currently fetching. Adding to render queue...",
        );
        await this.waitForPage(page, "fetch");
      } else {
        console.warn(
          `Infinite Grid | Render Page: Page ${page} is not ready (status: ${status}). Skipping render...`,
        );
        return;
      }
    }
    if (!this.isBackfilling) {
      console.debug("Infinite Grid | Update: Preloading images...");
      await this.preloadImages(content);
    }
    console.debug("Infinite Grid | Update: Images preloaded.");
    this.updateGridItems(page);
    const hasNextPage = this.updatePagination(content);
    this.pages[page].content = null;
    setTimeout(() => this.dispatchRenderEvent(page));
    this.rendering = false;
    (_b = this.link) == null ? void 0 : _b.classList.remove("loading");
    if (hasNextPage) {
      this.fetchOnIdle();
    }
  }
  getPageContent(page) {
    if (!page || !this.pages[page]) {
      console.warn(
        "Infinite Grid | Get Page Content: Invalid page number or page not found. Returning null.",
      );
      return null;
    }
    return this.getItemsFromContainer(this.pages[page].content);
  }
  dispatchRenderEvent(
    page,
    { backfill = false, backfillComplete = false } = {},
  ) {
    this.dispatchEvent(
      new CustomEvent("infinite-grid:render", {
        detail: {
          backfill,
          id: this.id,
          page,
          backfillComplete,
        },
      }),
    );
  }
  dispatchFetchEvent(page) {
    this.dispatchEvent(
      new CustomEvent("infinite-grid:fetch", {
        detail: {
          id: this.id,
          page,
        },
      }),
    );
  }
  async preloadImages(container) {
    if (!container) return;
    await imagesLoaded(
      this.getItemsFromContainer(container).map((item) =>
        item.querySelector("img"),
      ),
    );
  }
  fetchOnIdle() {
    requestIdleCallback(async () => {
      if (this.isLoading) return;
      await this.load({
        render: false,
      });
      this.prerenderFirstCards(this.nextPage);
    });
  }
  getItemId(element, page = this.page) {
    return (
      element.dataset[this.options.identifier] ??
      element.firstElementChild.dataset[this.options.identifier]
    );
  }
  getItemIds(elements = this.gridItemElements) {
    return elements.map((el) => this.getItemId(el));
  }
  updateGridItems(page) {
    console.debug(
      "Infinite Grid | Update Grid Items | Update triggered",
      page,
      this.pages,
    );
    const { content, prepend } = this.pages[page];
    if (!content) {
      console.warn(
        "Infinite Grid | Update Grid Items: No content found for page. Refetching...",
        page,
      );
      delete this.pages[page];
      this.load({
        page,
        render: true,
      });
      return;
    }
    const grid = this.getElementFromContainer(content, "grid");
    const existingItems = [...this.gridItemElements];
    const position = prepend ? "afterbegin" : "beforeend";
    this.gridElement.insertAdjacentHTML(position, grid.innerHTML);
    console.debug("Infinite Grid | Update Grid Items | Content appended");
    this.pages[page].status = PAGE_STATUSES.complete;
    console.debug(
      `Infinite Grid | Update Grid Items | Page ${page} status updated: ${this.pages[page].status}`,
    );
    const addedItems = this.gridItemElements.filter(
      (item) => !existingItems.includes(item),
    );
    this.setPageAnchor(addedItems[0], page);
    this.observeItems(addedItems);
    if (!prepend) {
      this.animateVisibleItems(addedItems);
    }
  }
  animateVisibleItems(items) {
    if (!items || items.length === 0) return;
    const visibleItems = [];
    const hiddenItems = [];
    gsapWithCSS.set(items, {
      autoAlpha: 0,
      scale: 0.9,
    });
    const visibilityObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            visibleItems.push(entry.target);
          } else {
            hiddenItems.push(entry.target);
          }
        });
        if (visibleItems.length + hiddenItems.length === items.length) {
          visibilityObserver.disconnect();
          if (hiddenItems.length > 0) {
            gsapWithCSS
              .set(hiddenItems, {
                autoAlpha: 1,
                scale: 1,
              })
              .then(() => {
                gsapWithCSS.set(hiddenItems, {
                  clearProps: "opacity,scale",
                });
              });
          }
          if (visibleItems.length > 0) {
            gsapWithCSS.fromTo(
              visibleItems,
              {
                autoAlpha: 0,
                scale: 0.9,
              },
              {
                autoAlpha: 1,
                scale: 1,
                duration: 1,
                ease: "power2.inOut",
                stagger: {
                  each: 0.1,
                  from: "start",
                  grid: "auto",
                },
                onComplete: () => {
                  visibleItems.forEach((item) => {
                    gsapWithCSS.set(item, {
                      clearProps: "opacity,visibility,scale",
                    });
                  });
                },
              },
            );
          }
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      },
    );
    items.forEach((item) => {
      visibilityObserver.observe(item);
    });
  }
  activateObserver() {
    if (!this.pagination) {
      console.debug(
        "Infinite Grid | No pagination element found, skipping observation.",
      );
      return;
    }
    this.observer.observe(this.pagination);
  }
  // TODO: CLEAN UP
  updatePagination(content) {
    var _a;
    console.debug("Infinite Grid | Updating pagination...");
    let newPagination = null;
    if (content) {
      newPagination = this.getElementFromContainer(content, "loadMore");
      if (this.loading || this.rendering)
        (_a = newPagination.querySelector("a")) == null
          ? void 0
          : _a.classList.add("loading");
    }
    if (newPagination) {
      const hasNextPage = this.pagination.update(
        newPagination,
        this.handleClick,
      );
      if (!hasNextPage) {
        this.end();
        return false;
      }
    }
    this.activateObserver();
    return true;
  }
  resetPagination() {
    const content = this.pages[1].content;
    this.updatePagination(content);
    this.setAttribute("page", 1);
    this.destroy();
    this.pages = {
      1: {
        content,
        status: PAGE_STATUSES.complete,
      },
    };
    this.init(true);
  }
  onClick(e) {
    e.preventDefault();
    const prerenderedCount = this.showPrerenderedItems();
    if (prerenderedCount > 0) {
      console.debug(
        `Infinite Grid | onClick: Showed ${prerenderedCount} pre-rendered items immediately`,
      );
      setTimeout(() => {
        this.renderPage();
      }, 100);
    } else {
      this.renderPage();
    }
  }
  end() {
    this.observer.disconnect();
  }
  destroy() {
    this.observer.disconnect();
  }
  // Pre-render first 4 cards of next page hidden for INP optimization
  async prerenderFirstCards(page = this.nextPage) {
    if (
      this.prerenderedPage === page ||
      this.isLoading ||
      this.prerenderedItems.length > 0
    ) {
      return;
    }
    console.debug(
      "Infinite Grid | Pre-render: Starting pre-render for page",
      page,
    );
    if (!this.pages[page] || this.pages[page].status !== PAGE_STATUSES.ready) {
      console.debug(
        "Infinite Grid | Pre-render: Page not ready, skipping pre-render",
      );
      return;
    }
    const pageContent = this.getPageContent(page);
    if (!pageContent || pageContent.length === 0) {
      console.debug(
        "Infinite Grid | Pre-render: No content available for pre-render",
      );
      return;
    }
    console.debug(
      "Infinite Grid | Pre-render: Page content available",
      pageContent,
    );
    const itemsToPrerender = pageContent.slice(
      0,
      Math.min(4, pageContent.length),
    );
    console.debug(
      `Infinite Grid | Pre-render: Pre-rendering ${itemsToPrerender.length} items`,
    );
    const images = itemsToPrerender
      .map((item) => item.querySelector("img"))
      .filter(Boolean);
    if (images.length > 0) {
      await imagesLoaded(images);
    }
    const existingItems = [...this.gridItemElements];
    itemsToPrerender.forEach((item) => {
      item.classList.add("prerendered");
      this.gridElement.appendChild(item);
      this.prerenderedItems.push(item);
    });
    const remainingItems = pageContent.slice(4);
    const gridContainer = this.getElementFromContainer(
      this.pages[page].content,
      "grid",
    );
    gridContainer.innerHTML = "";
    remainingItems.forEach((item) => gridContainer.appendChild(item));
    const addedItems = this.gridItemElements.filter(
      (item) => !existingItems.includes(item),
    );
    if (addedItems.length > 0) {
      this.observeItems(addedItems);
    }
    this.prerenderedPage = page;
    console.debug(
      "Infinite Grid | Pre-render: Pre-rendering complete for page",
      page,
    );
  }
  // Show pre-rendered items immediately for better INP
  showPrerenderedItems() {
    if (this.prerenderedItems.length === 0) {
      console.debug(
        "Infinite Grid | Show Pre-rendered: No pre-rendered items to show",
      );
      return false;
    }
    console.debug(
      `Infinite Grid | Show Pre-rendered: Showing ${this.prerenderedItems.length} pre-rendered items`,
    );
    gsapWithCSS.set(this.prerenderedItems, {
      scale: 0.9,
    });
    this.prerenderedItems.forEach((item) => {
      item.classList.remove("prerendered");
    });
    gsapWithCSS.to(this.prerenderedItems, {
      scale: 1,
      duration: 0.3,
      ease: "power2.out",
      stagger: 0.05,
      onComplete: () => {
        this.prerenderedItems.forEach((item) => {
          gsapWithCSS.set(item, {
            clearProps: "scale",
          });
        });
      },
    });
    const shownCount = this.prerenderedItems.length;
    this.prerenderedItems = [];
    this.prerenderedPage = null;
    return shownCount;
  }
}
if (!customElements.get("infinite-grid")) {
  customElements.define("infinite-grid", InfiniteGrid);
}
class LoadMore extends GWElement {
  get optionDefaults() {
    return {
      automatic: true,
    };
  }
  static get observedAttributes() {
    return ["automatic"];
  }
  get link() {
    return this.querySelector('[name="load-more"]');
  }
  get url() {
    var _a;
    return (
      ((_a = this.link) == null ? void 0 : _a.getAttribute("href")) ?? null
    );
  }
  connect(grid, { automatic = !grid.manual, onClick = grid.handleClick } = {}) {
    this.grid = grid;
    this.setAttribute("automatic", automatic);
    this.onClick = onClick;
  }
  update(element, onClick) {
    this.replace(element);
    if (onClick) {
      this.bindEvents(onClick);
    }
    return !!this.link;
  }
  replace(element) {
    if (element === null) {
      this.innerHTML = "";
      return;
    }
    if (!element) {
      console.info(
        'Skipped replacement of "load more" pagination element, no element provided.',
      );
      return;
    }
    this.innerHTML = element.innerHTML;
    return;
  }
  bindEvents(onClick = this.onClick) {
    var _a;
    if (!onClick) {
      console.warn(
        "Failed to bind LoadMore events, no onClick function provided.",
      );
      return;
    }
    (_a = this.link) == null ? void 0 : _a.addEventListener("click", onClick);
  }
  // toggleVisibility() {
  //   this.link.toggleAttribute('hidden', this.automatic)
  // }
  // init() {
  //   super.init()
  // }
}
if (!customElements.get("load-more")) {
  customElements.define("load-more", LoadMore);
}
